import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  Typography,
} from '@mui/material';
import { camelCase } from 'lodash-es';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  EvacZone,
  EvacZoneStatus,
  FormEvacZoneStatus,
  RegionEvacZoneStyle,
} from 'shared/types';
import { getEvacZoneDefaultDescription } from 'shared/utils';
import RichTextEditor from 'components/RichTextEditor';
import { makeStyles } from 'tss-react/mui';
import {
  EVAC_ZONE_LABEL_TRANS_KEYS,
  EVAC_ZONE_STATUS_ENABLED,
  EvacZoneStyles,
} from '../../../../constants';
import TwoSideMultiSelect from './TwoSideMultiSelect';

const useStyles = makeStyles()(() => ({
  editor: {
    minHeight: 42,
  },
}));

export const EvacZoneStatusLevelEditor = (props: {
  zoneStyles: RegionEvacZoneStyle[];
  temporarilyDisplayEvacZones: boolean;
  level: EvacZoneStatus;
  hasEvacZones: boolean;
  customName: string;
  levelEvacZones: FormEvacZoneStatus[];
  noStatusZones: EvacZone[];
  customEvacZones: boolean;
  customEvacZoneString?: string;
  control: ReturnType<typeof useFormContext>['control'];
  formState: ReturnType<typeof useFormContext>['formState'];
  toggleCustomZone: (enabled: boolean) => void;
}): JSX.Element | null => {
  const {
    zoneStyles,
    temporarilyDisplayEvacZones,
    levelEvacZones,
    level,
    hasEvacZones,
    noStatusZones,
    customName,
    customEvacZones,
    customEvacZoneString,
    control,
    formState,
    toggleCustomZone,
  } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const customFieldName = `evacuation${level.charAt(0).toUpperCase()}${camelCase(level.slice(1))}`;

  // Find first zone style that has the level enabled, iff no styles have the level enabled, don't render the component.
  // We are doing the check here to handle cases where the component is rendered in a context that is supporting both
  // ready_set_go and ready_set_shelter as possible styles. (e.g. on the board of CA and OR)
  const zoneStyle = zoneStyles.find(
    (style) => EVAC_ZONE_STATUS_ENABLED[style][level],
  );
  if (!zoneStyle) {
    return null;
  }

  // Filter noStatusZones to only include those that match the zoneStyle
  const noStatusZonesForLevel = noStatusZones.filter(
    (zone) =>
      EVAC_ZONE_STATUS_ENABLED[
        (zone.region.evacZoneStyle as RegionEvacZoneStyle) ||
          EvacZoneStyles.default
      ][level],
  );

  return (
    <>
      {hasEvacZones && (
        <>
          <Grid item xs={12}>
            <Typography
              component="h4"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {t(EVAC_ZONE_LABEL_TRANS_KEYS[zoneStyle][level])}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TwoSideMultiSelect
              disabled={temporarilyDisplayEvacZones}
              evacZoneStatus={level}
              availableZones={noStatusZonesForLevel}
              selectedZones={levelEvacZones}
              downgradeable
              upgradeable
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name={customName}
                  disabled={temporarilyDisplayEvacZones}
                  checked={Boolean(customEvacZones)}
                  onChange={(event) => {
                    toggleCustomZone(event.target.checked);
                  }}
                />
              }
              label={t(
                `geoEventEvacuations.inputs.${customFieldName}.enableCustom`,
              )}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <Controller
          name={customFieldName}
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const initialValue = customEvacZones
              ? customEvacZoneString
              : getEvacZoneDefaultDescription(levelEvacZones);
            return (
              <>
                <RichTextEditor
                  id={`field-control-evacuation-${level}`}
                  data-testid={`evacuation-${level}`}
                  label={t(EVAC_ZONE_LABEL_TRANS_KEYS[zoneStyle][level])}
                  initialValue={initialValue || ''}
                  onChange={field.onChange}
                  editable={
                    (!hasEvacZones || customEvacZones) &&
                    !temporarilyDisplayEvacZones &&
                    !formState.isSubmitting
                  }
                  error={Boolean(fieldState.error)}
                  editorClassName={classes.editor}
                  onBlur={field.onBlur}
                />
                {Boolean(fieldState.error) && (
                  <FormHelperText
                    error
                    sx={{ marginLeft: 1.75, marginRight: 1.75 }}
                  >
                    {fieldState.error?.message}
                  </FormHelperText>
                )}
              </>
            );
          }}
        />
      </Grid>
    </>
  );
};
